import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Image as ImgIcon, PlayCircle } from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, fileUrl } from "@/lib/api";

export default function Gallery() {
    const [albums, setAlbums] = useState([]);
    const [items, setItems] = useState({});
    const [category, setCategory] = useState("All");
    const [lightbox, setLightbox] = useState({ open: false, idx: 0, items: [] });

    useEffect(() => { api.get("/gallery/albums").then((r) => setAlbums(r.data)); }, []);

    const loadItems = async (albumId) => {
        if (items[albumId]) return items[albumId];
        const r = await api.get(`/gallery/albums/${albumId}/items`);
        setItems((prev) => ({ ...prev, [albumId]: r.data }));
        return r.data;
    };

    const openLightbox = async (album) => {
        const list = await loadItems(album.id);
        if (list.length === 0) return;
        setLightbox({ open: true, idx: 0, items: list });
    };

    const cats = ["All", ...Array.from(new Set(albums.map((a) => a.category)))];
    const filtered = category === "All" ? albums : albums.filter((a) => a.category === category);

    return (
        <PublicLayout>
            <section className="container-x pt-10">
                <Breadcrumbs items={[{ label: "Gallery" }]} />
                <div className="mt-5">
                    <div className="overline">Moments</div>
                    <h1 className="mt-3 font-serif text-5xl text-slate-900 sm:text-6xl">Gallery</h1>
                    <p className="mt-4 max-w-2xl text-lg text-slate-600">Snapshots from our events, meetings, festivals and community initiatives.</p>
                </div>
                <div className="mt-8 flex flex-wrap gap-2">
                    {cats.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCategory(c)}
                            className={`rounded-full border px-4 py-1.5 text-sm ${category === c ? "border-brand-blue bg-brand-blue text-white" : "border-slate-200 bg-white text-slate-700 hover:border-brand-blue hover:text-brand-blue"}`}
                            data-testid={`gallery-cat-${c}`}
                        >{c}</button>
                    ))}
                </div>
            </section>

            <section className="container-x mt-10 pb-20">
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((a) => (
                        <button
                            key={a.id}
                            onClick={() => openLightbox(a)}
                            className="surface-card-hover overflow-hidden text-left"
                            data-testid={`album-${a.id}`}
                        >
                            <div className="relative h-52 bg-brand-blue-light">
                                {a.cover_id ? (
                                    <img src={fileUrl(a.cover_id)} alt={a.title} className="h-full w-full object-cover" loading="lazy" />
                                ) : (
                                    <div className="grid h-full place-items-center text-brand-blue"><ImgIcon size={40} /></div>
                                )}
                                <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-blue">{a.category}</span>
                            </div>
                            <div className="p-5">
                                <div className="font-serif text-xl text-slate-900">{a.title}</div>
                                {a.description && <p className="mt-1 line-clamp-2 text-sm text-slate-600">{a.description}</p>}
                            </div>
                        </button>
                    ))}
                    {filtered.length === 0 && <div className="col-span-full py-16 text-center text-slate-500">No albums here yet.</div>}
                </div>
            </section>

            <Dialog open={lightbox.open} onOpenChange={(v) => !v && setLightbox({ open: false, idx: 0, items: [] })}>
                <DialogContent className="max-w-4xl bg-slate-900 p-0 text-white" data-testid="lightbox">
                    {lightbox.items.length > 0 && (() => {
                        const it = lightbox.items[lightbox.idx];
                        return (
                            <div className="relative">
                                <button
                                    className="absolute right-3 top-3 z-10 rounded-full bg-white/10 p-2 hover:bg-white/20"
                                    onClick={() => setLightbox({ open: false, idx: 0, items: [] })}
                                >
                                    <X size={16} />
                                </button>
                                {it.kind === "video" ? (
                                    <video src={fileUrl(it.file_id)} controls className="max-h-[80vh] w-full" />
                                ) : (
                                    <img src={fileUrl(it.file_id)} alt={it.caption} className="max-h-[80vh] w-full object-contain" />
                                )}
                                {lightbox.items.length > 1 && (
                                    <>
                                        <button
                                            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20"
                                            onClick={() => setLightbox((l) => ({ ...l, idx: (l.idx - 1 + l.items.length) % l.items.length }))}
                                        ><ChevronLeft size={18} /></button>
                                        <button
                                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20"
                                            onClick={() => setLightbox((l) => ({ ...l, idx: (l.idx + 1) % l.items.length }))}
                                        ><ChevronRight size={18} /></button>
                                    </>
                                )}
                                {it.caption && (
                                    <div className="border-t border-white/10 px-5 py-3 text-sm">{it.caption}</div>
                                )}
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </PublicLayout>
    );
}
